import { Deferred } from '@inertiajs/react'
import { useState } from 'react'
import { FirstResponseTimeCard } from '@/components/analytics/FirstResponseTimeCard'
import { ResponseTimeByCountryCard } from '@/components/analytics/ResponseTimeByCountryCard'
import { ResponseTimeWorldMap } from '@/components/analytics/ResponseTimeWorldMap'
import { PanelSkeleton } from '@/components/analytics/PanelSkeleton'
import { usePanel } from '@/components/analytics/usePanel'
import { cn } from '@/lib/utils'
import type { FirstResponseTimePanel, ResponseTimeByCountryPanel } from '@/types/analytics'

type Mode = 'absolute' | 'relative'

const MODE_PARAM = 'mode'

function readModeFromUrl(): Mode {
  const v = new URLSearchParams(window.location.search).get(MODE_PARAM)
  return v === 'relative' ? 'relative' : 'absolute'
}

// Push the mode into the URL without an Inertia roundtrip — both absolute
// and relative data already arrived with the panel, so this is purely a
// display preference. Using replaceState keeps the back button useful.
function writeModeToUrl(mode: Mode) {
  const url = new URL(window.location.href)
  if (mode === 'absolute') url.searchParams.delete(MODE_PARAM)
  else url.searchParams.set(MODE_PARAM, mode)
  window.history.replaceState({}, '', url.toString())
}

export function ResponseTimeSection() {
  const [mode, setModeState] = useState<Mode>(() => readModeFromUrl())
  const setMode = (next: Mode) => {
    setModeState(next)
    writeModeToUrl(next)
  }

  return (
    <>
      <ModeToggleBar mode={mode} setMode={setMode} />

      <Deferred data="first_response_time" fallback={<PanelSkeleton className="h-80" />}>
        <FirstResponseTimeDeferred mode={mode} />
      </Deferred>

      <Deferred data="response_time_by_country" fallback={<PanelSkeleton className="h-96" />}>
        <CountryPanelsDeferred mode={mode} />
      </Deferred>
    </>
  )
}

// Renders the absolute/business-hours toggle once at the top of the section.
// Reads has_support_hours from the FRT panel — the toggle only makes sense
// when there's a support-hours schedule to anchor "relative" against.
function ModeToggleBar({ mode, setMode }: { mode: Mode; setMode: (m: Mode) => void }) {
  const data = usePanel<FirstResponseTimePanel>('first_response_time')
  if (!data?.has_support_hours) return null
  return (
    <div className="flex justify-end">
      <div className="inline-flex rounded-md border border-border bg-muted/30 text-xs">
        <button
          type="button"
          onClick={() => setMode('absolute')}
          className={cn(
            'px-3 py-1.5 cursor-pointer rounded-l-md',
            mode === 'absolute' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          Wall-clock
        </button>
        <button
          type="button"
          onClick={() => setMode('relative')}
          className={cn(
            'px-3 py-1.5 cursor-pointer rounded-r-md',
            mode === 'relative' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          Business hours
        </button>
      </div>
    </div>
  )
}

function FirstResponseTimeDeferred({ mode }: { mode: Mode }) {
  const data = usePanel<FirstResponseTimePanel>('first_response_time')
  return data ? <FirstResponseTimeCard data={data} mode={mode} /> : null
}

function CountryPanelsDeferred({ mode }: { mode: Mode }) {
  const data = usePanel<ResponseTimeByCountryPanel>('response_time_by_country')
  if (!data) return null
  return (
    <>
      <ResponseTimeWorldMap data={data} mode={mode} />
      <ResponseTimeByCountryCard data={data} mode={mode} />
    </>
  )
}
